import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/api';
import { useOrg } from '../orgs/org-context';
import { toast } from 'react-toastify';

export default function GameSetup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeOrgId } = useOrg();

  const [model, setModel] = useState({
    name: '',
    status: 'draft',
    landingPageId: '',
    datasetId: '',
    renderCardDesignId: '',
    clues: []
  });

  // 🔥 dropdown data
  const [landingPages, setLandingPages] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [cards, setCards] = useState([]);

  // ---------------- LOAD DATA ----------------
  useEffect(() => {
    loadDropdowns();

    if (id && id !== 'new') {
      apiFetch(`/api/scavenger/item/${id}`).then(setModel);
    }
  }, [id]);

  const loadDropdowns = async () => {
    try {
      const [lpRes, dsRes, cardRes] = await Promise.all([
        apiFetch('/landingpages/getAll', { method: 'POST' }),
        apiFetch('/datasets/list', { method: 'POST' }),
        apiFetch('/cards/getAll')
      ]);


      setLandingPages(lpRes || []);
      setDatasets(dsRes.items || []);
      setCards(cardRes.items || []);
    } catch (err) {
      console.error('Failed loading dropdowns', err);
    }
  };

  // ---------------- SAVE ----------------
  const save = async () => {
    if (!model.name) {
      toast.error('Game name required');
      return;
    }

    if (!model.landingPageId) {
      toast.error('Landing page required');
      return;
    }

    if (!model.datasetId) {
      toast.error('Project required');
      return;
    }

    try {
      const payload = {
        ...model,
        orgId: activeOrgId
      };

      const res = await apiFetch('/api/scavenger/save', {
        method: 'POST',
        body: payload
      });

      toast.success('Game saved');

      navigate(`/game/edit/${res._id}`);
    } catch (err) {
      console.error('Save failed', err);
      toast.error(err.message || 'Save failed');
    }
  };

  // ---------------- CLUES ----------------

  const addClue = () => {
    setModel(prev => ({
      ...prev,
      clues: [
        ...prev.clues,
        {
          clueNo: prev.clues.length + 1,
          hintFront: '',
          revealText: ''
        }
      ]
    }));
  };

  const updateClue = (index, field, value) => {
    const next = [...model.clues];
    next[index][field] = value;
    setModel({ ...model, clues: next });
  };

  const removeClue = (index) => {
    const next = model.clues.filter((_, i) => i !== index)
      .map((c, i) => ({ ...c, clueNo: i + 1 })); // re-number

    setModel({ ...model, clues: next });
  };

  // ---------------- UI ----------------

  return (
    <div
      style={{
        padding: 16,
        maxWidth: 1200,
        height: 'calc(100vh - 90px)',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >

      {/* HEADER */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12
        }}
      >
        <h2 style={{ margin: 0 }}>
          Game Setup
        </h2>

        <div style={{ display: 'flex', gap: 10 }}>

          <button
            className="btn-gray"
            type="button"
            onClick={() => navigate('/game')}
          >
            Return
          </button>

          <button
            className="btn-green"
            type="button"
            onClick={save}
          >
            Save
          </button>

          <button
            className="btn-blue"
            type="button"
            disabled={!id || id === 'new'}
            onClick={() =>
              navigate(`/game/board/${id}`)
            }
          >
            View Board
          </button>

        </div>
      </div>

      {/* TOP SETUP ROW */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '220px 120px 1fr 1fr 1fr',
          gap: 10,
          alignItems: 'end',
          marginBottom: 14
        }}
      >

        <div>
          <label>Game Name</label>
          <input
            placeholder="Game Name"
            value={model.name}
            onChange={e =>
              setModel({
                ...model,
                name: e.target.value
              })
            }
            style={{ width: '100%' }}
          />
        </div>

        <div>
          <label>Status</label>
          <select
            value={model.status}
            onChange={e =>
              setModel({
                ...model,
                status: e.target.value
              })
            }
            style={{ width: '100%' }}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        <div>
          <label>Landing Page</label>
          <select
            value={model.landingPageId || ''}
            onChange={e =>
              setModel({
                ...model,
                landingPageId: e.target.value
              })
            }
            style={{ width: '100%' }}
          >
            <option value="">Select Landing Page</option>
            {landingPages.map(lp => (
              <option key={lp._id} value={lp._id}>
                {lp.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Project</label>
          <select
            value={model.datasetId || ''}
            onChange={e =>
              setModel({
                ...model,
                datasetId: e.target.value
              })
            }
            style={{ width: '100%' }}
          >
            <option value="">Select Project</option>
            {datasets.map(ds => (
              <option key={ds._id} value={ds._id}>
                {ds.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Card Design</label>
          <select
            value={model.renderCardDesignId || ''}
            onChange={e =>
              setModel({
                ...model,
                renderCardDesignId: e.target.value
              })
            }
            style={{ width: '100%' }}
          >
            <option value="">Select Card</option>
            {cards.map(c => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

      </div>

      {/* CLUE HEADER */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8
        }}
      >
        <strong>
          Clues
        </strong>

        <button
          className="btn-green"
          onClick={addClue}
        >
          + Add Clue
        </button>
      </div>

      {/* CLUE TABLE HEADER */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '70px 1fr 1fr 90px',
          gap: 10,
          fontWeight: 'bold',
          marginBottom: 6,
          paddingRight: 8
        }}
      >
        <div>#</div>
        <div>Hint</div>
        <div>Reveal Text</div>
        <div></div>
      </div>

      {/* SCROLLING CLUES */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingRight: 8,
          borderTop: '1px solid #ddd',
          paddingTop: 8
        }}
      >

        {model.clues.map((c, i) => (

          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '70px 1fr 1fr 90px',
              gap: 10,
              marginBottom: 8,
              alignItems: 'center'
            }}
          >

            <div>
              {c.clueNo}
            </div>

            <input
              placeholder="Hint"
              value={c.hintFront}
              onChange={e =>
                updateClue(
                  i,
                  'hintFront',
                  e.target.value
                )
              }
            />

            <input
              placeholder="Reveal Text"
              value={c.revealText}
              onChange={e =>
                updateClue(
                  i,
                  'revealText',
                  e.target.value
                )
              }
            />

            <button
              className="btn-red"
              onClick={() =>
                removeClue(i)
              }
            >
              Remove
            </button>

          </div>

        ))}

        <div style={{ marginTop: 12 }}>

        </div>

      </div>

    </div>
  );
}
